import { Component, Input, OnInit } from '@angular/core';
import { CustodianState } from '@models/ui/case-analytics-state/custodian-state.model';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { appear } from '../../animations/appear';
import { Router } from "@angular/router"
import { ThemeService as ChartThemeService } from 'ng2-charts';
import { LexbeThemeService as LexbeThemeService } from '@services/lexbe-theme.service';
import { CaseAnalyticsItemBaseComponent } from '../case-analytics-item-base.component';
import { ColorUtility } from '@utilities/color.utility';

@Component({
    selector: 'lex-case-analytics-custodians',
    templateUrl: './case-analytics-custodians.component.html',
    styleUrl: './case-analytics-custodians.component.scss',
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsCustodiansComponent extends CaseAnalyticsItemBaseComponent implements OnInit {
  @Input() set custodianState(state: CustodianState) {
    this.custodianState$.next(state);
  }

  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: { beginAtZero: true, grid: {offset: true} },
      y: { beginAtZero: true },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        enabled: false,
      }
    }
  };

  public ngChartPlugins = [{
    id: 'chartIsReady',
    afterRender: () => {
      if (this.barChartData !== null) {
        this.shouldShowLoader = false;
      }
    }
  }];


  public barChartType: ChartType = 'bar';
  public barChartLegend = true;

  public barChartLabels: string[] = null;
  public barChartData: ChartConfiguration['data']['datasets'] = null;
  public totalCustodianCount: number;
  public shouldShowLoader: boolean = true;

  private custodianState$ = new BehaviorSubject<CustodianState>(null);
  public custodianSummary: CustodianSummary = null;

  constructor(private router: Router,
    lexbeThemeService: LexbeThemeService,
    chartThemeService: ChartThemeService) {
    super(lexbeThemeService, chartThemeService);
  }

  public ngOnInit(): void {
    this.custodianState$.pipe(
      filter((response) => (!!response)),
      takeUntil(this.destroy$),
    ).subscribe(data => {
      if (data.custodians.length < 1) {
        this.noDataInCase$.next(true);
        return;
      }
      const sortedCustodianData = data.custodians.sort((a, b) => b.docCount - a.docCount).slice(0, 10);
      this.barChartLabels = sortedCustodianData.map(c => c.name);
      let gradientSteps = 5;
      if(this.barChartLabels.length > 5) {
        gradientSteps = this.barChartLabels.length;
      }
      const rgbStart = ColorUtility.rgbArrayToObject([13, 71, 161]);
      const rgbEnd =  ColorUtility.rgbArrayToObject([33, 150, 243]);
      const colors = ColorUtility.getRgbGradient(rgbStart, rgbEnd, gradientSteps);

      this.barChartData = [
        {
          data: sortedCustodianData.map(c => c.docCount),
          label: 'Number of Documents',
          backgroundColor: colors.map(c => ColorUtility.rgbObjectToString(c)),
          hoverBackgroundColor: colors.map(cObj => ColorUtility.rgbObjectToArray(cObj))
                                      .map(cArr => ColorUtility.getRgbHoverColor(cArr, 40))
                                      .map(cArr => ColorUtility.rgbArrayToString(cArr)),
          barThickness: 'flex',
          maxBarThickness: 20,
        },
      ];
      this.totalCustodianCount = data.caseCustodianCount;
    })
  }

  public displayCustodianSummary(chartEvent) {
    if (chartEvent.active.length === 1) {
      const i = chartEvent.active[0].index;
      const custodianDocCount = this.barChartData[0].data[i];
      const custodianName = this.barChartLabels[i];
      this.custodianSummary = {
        name: custodianName,
        assignedDocuments: custodianDocCount,
      };
    }
    else {
      this.custodianSummary = null;
    }
  }
}

interface CustodianSummary {
  name: string,
  assignedDocuments: any,
}
